
import {
  computed, defineComponent, PropType, ref, Ref,
} from 'vue';
import { useRouter } from 'vue-router';
import {
  IConversation,
  IGroupData,
  ISpacesContact,
} from '@/types/interfaces';
import {
  CONTACT_STATUS,
  GROUP_USER_STATUS,
  NETWORK_CONTACT_TYPES,
  PHOTO_VIEW_TYPE,
  USER_TYPE,
} from '@/types/enums';
import { useSpacesStore } from '../store/spacesStore';
import SpacesManagePopUp from '@/modules/spaces/components/SpacesManagePopUp.vue';
import { lowResUrlByViewTypeByUrl } from '@/helpers/lowResPhotoUrl';
import { useMainStore } from '@/store/mainStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';

export default defineComponent({
  components: {
    SpacesManagePopUp,
    AppLazyImage,
  },
  props: {
    user: { type: Object as () => ISpacesContact, required: true },
    userType: { type: Number as PropType<NETWORK_CONTACT_TYPES>, required: true },
    usersToAdd: { type: Array as () => string[], default: () => [] },
    usersToInvite: { type: Array as () => string[], default: () => [] },
    groupInfo: { type: Object as PropType<IGroupData>, required: false },
    publicCasesAmount: { type: Number, default: 0 },
  },
  emits: ['close-group-info', 'handle-user-update', 'accept-user-to-group', 'reject-user-to-group'],
  setup(props, { emit }) {
    const mainStore = useMainStore();
    const spacesStore = useSpacesStore();
    const messagesStore = useMessagesStore();
    const router = useRouter();
    const isClicked = ref(false);
    const showUserManage = ref(false);
    const showManagePopUp: Ref<boolean> = ref(false);

    const type: Ref<NETWORK_CONTACT_TYPES> = ref(props.userType);

    const fullName = computed(() => `${props.user.firstName} ${props.user.lastName}`);
    const photoUrl = computed(() => {
      if (props.user.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.user.photo.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });
    const currentUserIsAdmin = computed(
      () => messagesStore.currentConversation?.group?.members.find((member) => member.accountId === mainStore.user.accountId)?.type === GROUP_USER_STATUS.ADMIN,
    );

    function acceptContact(): void {
      spacesStore.acceptContact(props.user.accountId);
    }

    function rejectContact(): void {
      spacesStore.rejectContact(props.user.accountId);
    }

    function requestContact(): void {
      spacesStore.requestContact(props.user.accountId).then(() => {
        const currentMember = messagesStore.currentConversation?.group?.members.find((item) => item.accountId === props.user.accountId);
        if (currentMember) {
          currentMember.contactStatus = CONTACT_STATUS.REQUEST_SENT;
        }
      });
    }

    function deleteContact(): void {
      spacesStore.deleteContact(props.user.accountId);
    }

    function messageContact(): void {
      if (type.value === NETWORK_CONTACT_TYPES.ME || type.value === NETWORK_CONTACT_TYPES.OUTCOMING || type.value === NETWORK_CONTACT_TYPES.INVITATION) {
        return;
      }
      messagesStore.currentConversation = { info: { name: fullName.value } } as IConversation;
      router.push(`/messages/${props.user.accountId}`);
    }

    function acceptUserToGroup(): void {
      if (props.groupInfo?.id) {
        spacesStore.acceptUserToGroup(props.groupInfo.id, props.user.accountId);
        emit('accept-user-to-group', props.user.accountId);
      }
    }

    function rejectUserToGroup(): void {
      if (props.groupInfo?.id) {
        spacesStore.rejectUserToGroup(props.groupInfo.id, props.user.accountId);
        emit('reject-user-to-group', props.user.accountId);
      }
    }

    const isUserSelected = computed(() => props.usersToAdd?.includes(props.user.accountId) || props.usersToInvite?.includes(props.user.accountId));

    function viewProfile(): void {
      if (type.value === NETWORK_CONTACT_TYPES.ME) {
        return;
      }
      router.push(`/profile/${props.user.accountId}?publicCasesAmount=${props.publicCasesAmount}`);
    }

    function handleUserUpdate(): void {
      showManagePopUp.value = false;
      emit('handle-user-update');
    }

    return {
      fullName,
      photoUrl,
      type,
      spacesStore,
      NETWORK_CONTACT_TYPES,
      GROUP_USER_STATUS,
      CONTACT_STATUS,
      USER_TYPE,
      isClicked,
      isUserSelected,
      showUserManage,
      showManagePopUp,
      mainStore,
      requestContact,
      acceptContact,
      rejectContact,
      messageContact,
      deleteContact,
      viewProfile,
      handleUserUpdate,
      acceptUserToGroup,
      rejectUserToGroup,
      currentUserIsAdmin,
    };
  },
});
